// filepath: cryptobag-com/src/components/GlobalMarketTicker.jsx
import React from 'react';
import { useApi } from '../hooks/useApi';
import { formatCompactNumber } from '../utils/formatters';

/**
 * GlobalMarketTicker Component
 * Displays a sticky bar with global market data (Market Cap, Volume, BTC Dominance).
 */
const GlobalMarketTicker = () => {
  const { data: globalData, loading, error } = useApi('/api/global');

  const TickerStat = ({ label, value, change }) => (
    <div className="flex items-center space-x-2 px-4">
      <span className="text-xs font-medium text-gray-400">{label}:</span>
      <span className="text-sm font-medium text-white">{value}</span>
      {change !== undefined && (
        <span className={`text-xs font-medium ${change > 0 ? 'text-green-500' : 'text-red-500'}`}>
          {change > 0 ? '▲' : '▼'} {change.toFixed(2)}%
        </span>
      )}
    </div>
  );

  const TickerSkeleton = () => (
    <div className="flex items-center space-x-4 animate-pulse">
      <div className="flex items-center space-x-2 px-4">
        <div className="h-3 bg-gray-700 rounded w-16"></div>
        <div className="h-4 bg-gray-700 rounded w-24"></div>
      </div>
      <div className="flex items-center space-x-2 px-4">
        <div className="h-3 bg-gray-700 rounded w-16"></div>
        <div className="h-4 bg-gray-700 rounded w-24"></div>
      </div>
      <div className="flex items-center space-x-2 px-4">
        <div className="h-3 bg-gray-700 rounded w-16"></div>
        <div className="h-4 bg-gray-700 rounded w-24"></div>
      </div>
    </div>
  );

  const TickerContent = () => {
    if (loading || !globalData) {
      return <TickerSkeleton />;
    }
    
    if (error) {
      return <div className="text-red-500 text-xs px-4">Failed to load global market data.</div>;
    }

    const { data } = globalData;
    const marketCap = formatCompactNumber(data.total_market_cap.usd);
    const volume = formatCompactNumber(data.total_volume.usd);
    const btcDominance = data.market_cap_percentage.btc.toFixed(2);
    const marketCapChange = data.market_cap_change_percentage_24h_usd;

    return (
      <div className="flex items-center divide-x divide-gray-700">
        <TickerStat label="Market Cap" value={marketCap} change={marketCapChange} />
        <TickerStat label="24h Volume" value={volume} />
        <TickerStat label="BTC Dominance" value={`${btcDominance}%`} />
      </div>
    );
  };

  return (
    <div className="bg-gray-800/90 border-b border-gray-700/50 backdrop-blur-md sticky top-[65px] z-40 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center">
        {/* Use a simple marquee-like effect with animation */}
        <div className="flex-shrink-0 animate-marquee whitespace-nowrap">
          <TickerContent />
        </div>
        <div className="flex-shrink-0 animate-marquee whitespace-nowrap" aria-hidden="true">
          <TickerContent />
        </div>
      </div>
    </div>
  );
};

export default GlobalMarketTicker;
